import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "@src/api/config";
import { showModalSuccess } from "@src/redux/modalTypeSuccess";
import { AppDispatch, RootState } from "@src/redux/store";
import { CurrenciesIDEnum } from "@src/views/Balances/Components/currencies.types";
import { DOCUMENT_TYPES_BY_ENTITY } from "@src/utility/documentTypes";
import { fileToBase64 } from "@src/utility/fileToBase64";

const urlBase = "api/contractors";

type RequestParams = Record<string, any>;

export interface RCContractorInvitePayload {
  email: string;
}

export interface RCContractorState {
  data: any;
  suggestions: any[];
  contractor: any | null;
  params: RequestParams;
  meta: any;
  total: number;
  sendingInvite: boolean;
  countries: any[];
  states: any[];
  cities: any[];
}

export const getRCContractors = createAsyncThunk<
  any,
  RequestParams | undefined,
  { state: RootState }
>("rcContractors/getAll", async (params = {}, { getState }) => {
  const state = getState();
  const { productSelected } = state.products || {};
  const { businessLineSelected } = state.businessLines || {};

  const query = {
    ...params,
    product: productSelected?.value,
    business_line: businessLineSelected?.id
  };

  const response = await api.get(urlBase, { params: query });
  return {
    params: query,
    data: response.data
  };
});

export const getRCContractorSuggestions = createAsyncThunk<
  any,
  RequestParams | undefined,
  { state: RootState }
>("rcContractors/getSuggestions", async (params = {}, { getState }) => {
  const state = getState();
  const { productSelected } = state.products || {};
  const { businessLineSelected } = state.businessLines || {};

  const query = {
    ...params,
    product: productSelected?.value,
    business_line: businessLineSelected?.id
  };

  const response = await api.get(`${urlBase}/suggestions`, { params: query });
  return {
    data: response.data
  };
});

export const getRCContractorById = createAsyncThunk<any, number | string>(
  "rcContractors/getById",
  async (id: number | string) => {
    const response = await api.get(`${urlBase}/${id}`);
    return {
      data: response.data
    };
  }
);

export const sendRCContractorInvite = createAsyncThunk<
  any,
  RCContractorInvitePayload,
  { dispatch: AppDispatch; state: RootState }
>("rcContractors/sendInvite", async ({ email }, { dispatch, getState }) => {
  const state = getState();
  const { productSelected } = state.products || {};
  const { businessLineSelected } = state.businessLines || {};

  const body = {
    email,
    product: productSelected?.value,
    business_line: businessLineSelected?.id
  };

  const response = await api.post(`${urlBase}/send_mail`, body);

  if (response.data.status === "success") {
    await dispatch(showModalSuccess({ message: response.data.message }));
  }

  return {
    data: response.data
  };
});

export const getCountriesOptions = createAsyncThunk(
  "rcContractors/getCountries",
  async () => {
    const response = await api.get("api/countries", { params: { filter: "all" } });
    return {
      data: response.data
    };
  }
);

export const getStatesOptions = createAsyncThunk(
  "rcContractors/getStates",
  async (countryId: number | string) => {
    const response = await api.get(`api/states/${countryId}`);
    return {
      data: response.data
    };
  }
);

export const getCitiesOptions = createAsyncThunk(
  "rcContractors/getCities",
  async (stateId: number | string) => {
    const response = await api.get(`api/cities/${stateId}`);
    return {
      data: response.data
    };
  }
);

export const createPublicContractor = createAsyncThunk<
  any,
  any,
  { dispatch: AppDispatch; state: RootState }
>("rcContractors/createPublic", async (payload, { dispatch, getState }) => {
  const state = getState();
  const { productSelected } = state.products || {};
  const { businessLineSelected } = state.businessLines || {};

  const mappedPayload = {
    token: payload.token,
    name_contractor: payload.nombre_contratista,
    status: true,
    giro: payload.giro,
    contrato: payload.contrato,
    email: payload.correo_electronico,
    user_name: payload.contacto_nombres,
    phone: payload.telefono,
    password: payload.password,
    password_confirmation: payload.password_confirmation,
    seller_id: payload.seller_id,
    provider_id: payload.provider_id,
    business_line_id: businessLineSelected?.id,
    product_id: productSelected?.value,
    procedencia: "mexican",
    billing_type: payload.tipo_entidad,
    razon_social: payload.razon_social,
    regimen_juridico: payload.tipo_entidad,
    nombres: payload.contacto_nombres,
    apellido_paterno: payload.contacto_apellido_paterno,
    apellido_materno: payload.contacto_apellido_materno,
    pais: payload.pais,
    estado: payload.estado,
    ciudad: payload.ciudad,
    localidad: payload.localidad,
    codigo_postal: payload.codigo_postal,
    colonia: payload.colonia || "NA",
    calle_numero: payload.calle_numero,
    tax_id: payload.tax_id,
    rfc: payload.rfc,
    uso_cfdi: 26,
    regimen_fiscal: payload.regimen_fiscal,
    product: productSelected?.value,
    business_line: businessLineSelected?.id,
    currency_id: CurrenciesIDEnum.MXN
  };

  const entityType = payload.tipo_entidad === "moral" ? "moral" : "physical";
  const documentsMap = DOCUMENT_TYPES_BY_ENTITY[entityType];
  const documentEntries = Object.entries(documentsMap);
  const documents = await Promise.all(
    documentEntries.map(async ([fieldName, documentType]) => {
      const file = payload[fieldName];
      if (!file || !documentType) return null;
      const base64 = await fileToBase64(file);
      return {
        document_type: documentType,
        original_name: file.name || fieldName,
        file: [base64]
      };
    })
  );

  const requestPayload = {
    ...mappedPayload,
    documents: documents.filter(Boolean)
  };

  const response = await api.post(`${urlBase}`, requestPayload);

  if (response.data.status === "success") {
    await dispatch(showModalSuccess({ message: response.data.message, url: "/rc/contractors" }));
  }

  return {
    data: response.data
  };
});

export const updateRCContractor = createAsyncThunk<
  any,
  { id: number | string; payload: any; returnUrl?: string },
  { dispatch: AppDispatch; state: RootState }
>("rcContractors/update", async ({ id, payload, returnUrl }, { dispatch, getState }) => {
  const state = getState();
  const { productSelected } = state.products || {};
  const { businessLineSelected } = state.businessLines || {};

  const mappedPayload = {
    name_contractor: payload.nombre_contratista,
    status: true,
    giro: payload.giro,
    contrato: payload.contrato,
    email: payload.correo_electronico,
    user_name: payload.contacto_nombres,
    phone: payload.telefono,
    password: payload.password || undefined,
    seller_id: payload.seller_id,
    provider_id: payload.provider_id,
    business_line_id: businessLineSelected?.id,
    product_id: productSelected?.value,
    procedencia: "mexican",
    billing_type: payload.tipo_entidad,
    razon_social: payload.razon_social,
    regimen_juridico: payload.tipo_entidad,
    nombres: payload.contacto_nombres,
    apellido_paterno: payload.contacto_apellido_paterno,
    apellido_materno: payload.contacto_apellido_materno,
    pais: payload.pais,
    estado: payload.estado,
    ciudad: payload.ciudad,
    localidad: payload.localidad,
    codigo_postal: payload.codigo_postal,
    colonia: payload.colonia || "NA",
    calle_numero: payload.calle_numero,
    tax_id: payload.tax_id,
    rfc: payload.rfc,
    uso_cfdi: 26,
    regimen_fiscal: payload.regimen_fiscal,
    product: productSelected?.value,
    business_line: businessLineSelected?.id,
    currency_id: CurrenciesIDEnum.MXN
  };

  const sanitizedPayload = { ...mappedPayload } as Record<string, any>;
  Object.keys(sanitizedPayload).forEach((key) => {
    if (typeof sanitizedPayload[key] === "undefined") {
      delete sanitizedPayload[key];
    }
  });

  const entityType = payload.tipo_entidad === "moral" ? "moral" : "physical";
  const documentsMap = DOCUMENT_TYPES_BY_ENTITY[entityType];
  const documentEntries = Object.entries(documentsMap);
  const documents = await Promise.all(
    documentEntries.map(async ([fieldName, documentType]) => {
      const file = payload[fieldName];
      const isFile =
        file instanceof File || (file && typeof file === "object" && file.name);
      if (!isFile || !documentType) return null;
      const base64 = await fileToBase64(file);
      return {
        document_type: documentType,
        original_name: file.name || fieldName,
        file: [base64]
      };
    })
  );

  const requestPayload = {
    ...sanitizedPayload,
    documents: documents.filter(Boolean)
  };

  const response = await api.put(`${urlBase}/${id}`, requestPayload);

  if (response.data.status === "success") {
    await dispatch(
      showModalSuccess({ message: response.data.message, url: returnUrl ?? "/rc/contractors" })
    );
  }

  return {
    data: response.data
  };
});

const initialState: RCContractorState = {
  data: [],
  suggestions: [],
  contractor: null,
  params: {},
  meta: {},
  total: 0,
  sendingInvite: false,
  countries: [],
  states: [],
  cities: []
};

export const rcContractorsSlice = createSlice({
  name: "rcContractors",
  initialState,
  reducers: {
    resetRCContractors: (state) => {
      state.data = [];
      state.suggestions = [];
      state.contractor = null;
      state.params = {};
      state.meta = {};
      state.total = 0;
      state.sendingInvite = false;
    },
    clearRCContractor: (state) => {
      state.contractor = null;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getRCContractors.fulfilled, (state, action) => {
      const payloadData = action.payload;
      if (action.payload?.params?.format === "options") {
        state.suggestions = payloadData.data;
      } else {
        if (Array.isArray(payloadData)) {
          state.data = {
            data: payloadData,
            meta: { last_page: 1, total: payloadData.length }
          };
          state.meta = { last_page: 1, total: payloadData.length };
          state.total = payloadData.length;
        } else {
          state.data = payloadData.data;
          state.meta = payloadData?.meta;
          state.total = payloadData?.meta?.total;
        }
      }
      state.params = action.payload.params;
    });
    builder.addCase(getRCContractorSuggestions.fulfilled, (state, action) => {
      state.suggestions = action.payload.data;
    });
    builder.addCase(getRCContractorById.fulfilled, (state, action) => {
      state.contractor = action.payload?.data?.data ?? action.payload?.data ?? null;
    });
    builder.addCase(sendRCContractorInvite.pending, (state) => {
      state.sendingInvite = true;
    });
    builder.addCase(sendRCContractorInvite.fulfilled, (state) => {
      state.sendingInvite = false;
    });
    builder.addCase(sendRCContractorInvite.rejected, (state) => {
      state.sendingInvite = false;
    });
    builder.addCase(getCountriesOptions.fulfilled, (state, action) => {
      state.countries = action.payload.data;
    });
    builder.addCase(getStatesOptions.fulfilled, (state, action) => {
      state.states = action.payload.data;
    });
    builder.addCase(getCitiesOptions.fulfilled, (state, action) => {
      state.cities = action.payload.data;
    });
  }
});

export const { resetRCContractors, clearRCContractor } = rcContractorsSlice.actions;

export default rcContractorsSlice.reducer;
